import { ArrowsPointingInIcon, ArrowsPointingOutIcon } from "@heroicons/react/24/outline";
import { scrollToElementById } from "@utils/utils";
import { useFlags } from "launchdarkly-react-client-sdk";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";

import { Button, Divider, Paragraph, SelectItem, Small } from "@fronterahealth/frontera-ui-components";

import {
  ApiReportTemplateExportFrameworkChoices,
  ExportTypeEnum,
  LearnerFileType,
  ReportTypeEnums,
  useLearnerManualGoalSyncByIdQuery,
} from "@api/graphql/types-and-hooks";
import { Loader } from "@components/ClinicalNewReportDialog/Loader";
import { isFieldContentEmpty } from "@components/FieldComponent/utils";
import { LayoutAside, LayoutBody, LayoutMain } from "@components/Layout";
import { useIsQaScannerEnabled } from "@components/QaReview/hooks/useIsQaScannerEnabled";
import { RegenerateReportButton } from "@components/RegenerateReportPart/RegenerateReportButton";
import { ReportCompletionDialog } from "@components/ReportCompletionAndExport/ReportCompletionDialog";
import { EXPORT_OPTIONS, ReportExportDialog } from "@components/ReportCompletionAndExport/ReportExportDialog";
import { ReportSection } from "@components/ReportField/ReportSection";
import { SIGNATURE_SECTION_IDENTIFIER } from "@components/ReportField/consts";
import { ReportSettings } from "@components/ReportSettings/ReportSettings";
import { notifyWarn } from "@components/notifications/notifications";
import { useReportExport } from "@hooks/useReportExport";
import { useSignatureHandler } from "@hooks/useSignatureHandler";
import { useTemplateExport } from "@hooks/useTemplateExport";
import { AssessmentReportSearch } from "@pages/ClinicalReportDetails/Components/AssessmentFiles/AssessmentReportSearch";
import { AssessmentReportsCollapsible } from "@pages/ClinicalReportDetails/Components/AssessmentFiles/AssessmentReportsCollapsible";
import { AssessmentSlideOver } from "@pages/ClinicalReportDetails/Components/AssessmentFiles/AssessmentSlideOver";
import { PreviousAssessmentReportCollapsible } from "@pages/ClinicalReportDetails/Components/AssessmentFiles/PreviousAssessmentReportCollapsible";
import { ClinicalReportFooterButtonRow } from "@pages/ClinicalReportDetails/Components/ClinicalReportFooterButtonRow";
import { ReviewHeader } from "@pages/ClinicalReportDetails/Components/ReviewHeader";
import { StickyNavCollapsible } from "@pages/ClinicalReportDetails/Components/StickyNavCollapsible";
import { useFocusedFieldManager } from "@pages/ClinicalReportDetails/Providers/FocusedFieldProvider";
import { DiagnosisBuilderSections } from "@pages/ClinicalReportDetails/ReportPage/DiagnosisBuilderSections";
import { useFooterPrimaryButtonState } from "@pages/ClinicalReportDetails/ReportPage/hooks/useFooterPrimaryButtonState";
import { useNavigateToReview } from "@pages/ClinicalReportDetails/ReportPage/hooks/useNavigateToReview";
import { isGeneratingSection } from "@pages/ClinicalReportDetails/helper";
import { useClinicalReportDetailsData } from "@providers/ClinicalReportDetailsProvider";
import { useReport } from "@providers/ReportProvider";

export const ReportPage: React.FC = () => {
  const {
    reportSections,
    isReportCompleted,
    previousAssessmentReports,
    reportType,
    isReassessment,
    isEHRScraperUser,
    isReportPage,
    activeTab,
  } = useClinicalReportDetailsData();
  const { report, reportQuery } = useReport();

  const isDiagnosisEvaluation = reportType === ReportTypeEnums.DiagnosisEvaluation;
  const flags = useFlags();
  const isSectionGenerating = isGeneratingSection(reportSections);

  const [collapseFields, setCollapseFields] = useState({ value: false }); // use object to trigger change even if the value hasn't actually changed.

  const isReportQaScannerEnabled = useIsQaScannerEnabled();
  const footerPrimaryButton = useFooterPrimaryButtonState(isSectionGenerating, isReportQaScannerEnabled);
  const isDbSinglePage = flags["db-single-page"];

  // refetch on mount
  useEffect(() => {
    if (isReassessment) {
      reportQuery.refetch();
    }
  }, []);

  // Filter sections by page location for db-single-page view
  const { regularSections, recommendationsSections, conclusionsSections, appendixSections, signatureSection } =
    useMemo(() => {
      if (!isDbSinglePage || !isDiagnosisEvaluation) {
        return {
          regularSections: reportSections,
          recommendationsSections: [],
          conclusionsSections: [],
          appendixSections: [],
          signatureSection: null,
        };
      }

      const signature = reportSections.find((s) => s.sectionIdentifier === SIGNATURE_SECTION_IDENTIFIER) ?? null;
      const nonSignature = reportSections.filter((s) => s.sectionIdentifier !== SIGNATURE_SECTION_IDENTIFIER);

      const regular = nonSignature.filter(
        (s) =>
          s.pageLocation !== "recommendations" && s.pageLocation !== "conclusions" && s.pageLocation !== "appendix",
      );
      const recommendations = nonSignature.filter((s) => s.pageLocation === "recommendations");
      const conclusions = nonSignature.filter((s) => s.pageLocation === "conclusions");
      const appendix = nonSignature.filter((s) => s.pageLocation === "appendix");

      return {
        regularSections: regular,
        recommendationsSections: recommendations,
        conclusionsSections: conclusions,
        appendixSections: appendix,
        signatureSection: signature,
      };
    }, [reportSections, isDbSinglePage, isDiagnosisEvaluation]);

  // State for direct sign flow (when FF is off)
  const [isCompletionDialogOpen, setIsCompletionDialogOpen] = useState<boolean>(false);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState<boolean>(false);
  const [selectedExportType, setSelectedExportType] = useState<SelectItem>(EXPORT_OPTIONS[0]);

  const clientName = `${report?.reportClientDetails?.clientFirstName || "N/A"} ${report?.reportClientDetails?.clientLastName || ""}`;
  const exportFramework = report?.reportTemplate?.exportFramework;
  const isDocxTemplaterExport = exportFramework === ApiReportTemplateExportFrameworkChoices.Docxtemplater;
  const isHandlebarsExport = exportFramework === ApiReportTemplateExportFrameworkChoices.Handlebars;
  const isUnifiedExport = report?.reportTemplate?.unifiedExport ?? false;
  // The template's `unifiedExport` field forces docxtemplater orgs through the
  // handlebars server-side export pipeline.
  const useHandlebarsPath = isHandlebarsExport || (isDocxTemplaterExport && isUnifiedExport);
  const useDocxTemplaterPath = isDocxTemplaterExport && !isUnifiedExport;

  const learnerId = report?.learner?.id || "";

  const { data: importStatusData, isFetching: isImportStatusFetching } = useLearnerManualGoalSyncByIdQuery(
    { learnerId },
    {
      enabled: !!learnerId && isReassessment,
    },
  );

  const { handleExport, isExporting } = useReportExport({
    report: report,
    exportType: selectedExportType.id as ExportTypeEnum,
    clientName,
    onExportComplete: () => setIsExportModalOpen(false),
  });

  const { handleExport: handleDocxTemplaterExport, isExporting: isDocxTemplaterExporting } = useTemplateExport({
    reportData: report,
    onExportComplete: () => {
      setIsCompletionDialogOpen(false);
    },
  });

  const onSignReport = async () => {
    if (!useDocxTemplaterPath) {
      // Dont auto set for docxtemplater since export is just-in-time on frontend
      setIsGenerating(true);
    }
    setIsCompletionDialogOpen(true);
  };

  const onSignSuccess = () => {
    setIsGenerating(false);
  };

  const onSignError = () => {
    setIsGenerating(false);
    setIsCompletionDialogOpen(false);
  };

  const { handleSignReport: handleInlineSignReport } = useSignatureHandler({
    report: report,
    isCompleted: isReportCompleted,
    refetchData: reportQuery.refetch,
    onSignSuccess,
    onSignError,
  });

  const handleInlineSign = async () => {
    await onSignReport();
    await handleInlineSignReport();
  };

  const { navigateToReview } = useNavigateToReview(report);

  const focusManager = useFocusedFieldManager();

  // Used by the legacy ClinicalReportFooterButtonRow on non-Reassessment
  // reports (IA / DiagnosisEvaluation). Reassessment Narrative gates via the
  // disabled Next Step button on ReassessmentStepProgressNav and never hits
  // this path.
  const handleNavigateToReview = useCallback(async () => {
    const missingRequired = reportSections
      .flatMap((s) => s.sectionField?.edges ?? [])
      .map((e) => e?.node)
      .filter((f) => !!f?.templateSectionField?.isRequired && isFieldContentEmpty(f.fieldType, f.content));

    if (missingRequired.length > 0) {
      focusManager?.setShowRequiredErrors(true);
      missingRequired.forEach((f) => notifyWarn(`${f?.title ? f.title : "Field"} is required.`));
      scrollToElementById(missingRequired[0]!.id);
      return;
    }
    focusManager?.setShowRequiredErrors(false);
    await navigateToReview();
  }, [reportSections, navigateToReview, focusManager]);

  const handlePreviousAssessmentClicked = (learnerFile: LearnerFileType) => {
    let pdfFile;
    if (learnerFile.mimeType === "application/pdf") {
      pdfFile = learnerFile;
    } else {
      pdfFile = previousAssessmentReports.find(
        (report) => report.parentFile?.id === learnerFile.id && report.mimeType === "application/pdf",
      );
    }
    if (pdfFile) {
      setAssessmentFileForSearch(pdfFile);
      setAssessmentSearchOpen(true);
    }
  };

  // Handler for diagnosis evaluation file clicks - opens PDF files in drawer view
  const handleDiagnosisFileClicked = (learnerFile: LearnerFileType) => {
    if (learnerFile.mimeType === "application/pdf") {
      setAssessmentFileForSearch(learnerFile);
      setAssessmentSearchOpen(true);
    }
    // Non-PDF files will be downloaded via the FilesCollapsible's downloadFileCallback
  };

  const [assessmentSearchOpen, setAssessmentSearchOpen] = useState<boolean>(false);
  const [assessmentFileForSearch, setAssessmentFileForSearch] = useState<LearnerFileType | null>(null);

  if (isEHRScraperUser && isReassessment && !!learnerId && (isImportStatusFetching || !importStatusData)) {
    return (
      <div className="flex justify-center items-center h-full">
        <Loader className="w-4 h-4" />
      </div>
    );
  }

  // Only redirect when on the legacy report-page route; narrative tab must remain accessible
  if (isReportPage && isReassessment) {
    return <Navigate to="../programming" replace relative="path" />;
  }

  const signatureSectionBlock =
    isDbSinglePage && isDiagnosisEvaluation && !!signatureSection ? (
      <ReportSection section={signatureSection} isCollapsed={collapseFields} disabled={isReportCompleted} />
    ) : null;

  return (
    <>
      <LayoutBody id="ClinicalReport">
        <LayoutMain className="flex flex-col gap-2">
          {/* The high-level "Review & Edit Report" guidance card duplicates
                the new stepper on the Narrative step (its 3 sub-cards mirror
                Goal Progress / Narrative / Review). Hide it there; keep it
                on Goal Progress where users still benefit from the intro. */}
          {!isReportCompleted && !isDiagnosisEvaluation && !(isReassessment && activeTab === "narrative") && (
            <ReviewHeader />
          )}
          <div className="flex items-center justify-between py-4">
            {report?.reportTemplate?.isBaseTemplate && (
              <ReportSettings reportIsCompleted={isReportCompleted} report={report} refetchData={reportQuery.refetch} />
            )}
            <div className="flex space-x-2">
              {!isReportCompleted && (
                <RegenerateReportButton
                  reportId={report?.id ?? ""}
                  disabled={isSectionGenerating}
                  onRegenerated={() => reportQuery.refetch()}
                />
              )}
              <Button
                onClick={() => setCollapseFields({ value: true })}
                text={
                  <div className="flex items-center text-rio-700">
                    <ArrowsPointingInIcon className="mr-1 h-4 w-4" />
                    <Small colorType="primary" className="text-rio-700">
                      Contract All
                    </Small>
                  </div>
                }
                appearance="link"
                className="ml-2 text-right"
              />
              <Button
                onClick={() => setCollapseFields({ value: false })}
                text={
                  <div className="flex items-center text-rio-700">
                    <ArrowsPointingOutIcon className="mr-1 h-4 w-4" />
                    <Small colorType="primary" className="text-rio-700">
                      Expand All
                    </Small>
                  </div>
                }
                appearance="link"
                className="ml-2 "
              />
            </div>
          </div>
          {/* Regular sections (report-part-1, report-part-2) */}
          {regularSections.map((section) => {
            return (
              <ReportSection
                key={section.id}
                section={section}
                isCollapsed={collapseFields}
                disabled={isReportCompleted}
              />
            );
          })}
          {/* DB Single Page: Recommendations and Conclusions sections */}
          {isDbSinglePage && isDiagnosisEvaluation && (
            <DiagnosisBuilderSections
              recommendationsSections={recommendationsSections}
              conclusionsSections={conclusionsSections}
              isReportCompleted={isReportCompleted}
              isSectionGenerating={isSectionGenerating}
              collapseFields={collapseFields}
            />
          )}
          {/* DB Single Page: Appendix sections */}
          {isDbSinglePage && isDiagnosisEvaluation && appendixSections.length > 0 && (
            <>
              <div className="flex items-center gap-4 my-6">
                <Divider className="flex-1" />
                <Paragraph displayType="loud" colorType="secondary">
                  Appendix
                </Paragraph>
                <Divider className="flex-1" />
              </div>
              {appendixSections.map((section) => (
                <ReportSection
                  key={section.id}
                  section={section}
                  isCollapsed={collapseFields}
                  disabled={isReportCompleted}
                />
              ))}
            </>
          )}
          {/* DB Single Page: Signature section (template-driven) */}
          {signatureSectionBlock}
        </LayoutMain>
        <LayoutAside className="mt-8 pt-2 flex flex-col gap-2">
          {!isDiagnosisEvaluation && (
            <PreviousAssessmentReportCollapsible handleFileClicked={handlePreviousAssessmentClicked} />
          )}
          <AssessmentReportsCollapsible
            handleFileClicked={isDiagnosisEvaluation ? handleDiagnosisFileClicked : undefined}
          />
          <StickyNavCollapsible />
        </LayoutAside>
      </LayoutBody>
      {!isReportCompleted && !isReassessment && (
        <ClinicalReportFooterButtonRow
          primaryButtonDisabled={footerPrimaryButton.disabled}
          primaryButtonDisabledTooltipReason={footerPrimaryButton.reason}
          primaryButtonTitle={isReportQaScannerEnabled ? "Continue to Review" : "Sign and Complete"}
          hideBackButton
          primaryButtonAction={isReportQaScannerEnabled ? handleNavigateToReview : handleInlineSign}
          skipNavigate
          ehrSyncAlerts={report?.ehrSyncAlerts}
        />
      )}

      {/* Dialogs for direct sign flow (when QA scanner FF is off) */}
      {!isReportQaScannerEnabled && (
        <>
          <ReportCompletionDialog
            open={isCompletionDialogOpen}
            clientName={clientName}
            clientId={report?.learner?.id}
            onExportReport={() => {
              if (useDocxTemplaterPath) {
                handleDocxTemplaterExport();
                return;
              }

              setIsCompletionDialogOpen(false);
              if (useHandlebarsPath) {
                setIsExportModalOpen(true);
              } else {
                handleExport();
              }
            }}
            onViewCompletedReport={() => {
              setIsCompletionDialogOpen(false);
            }}
            isGenerating={isGenerating || isDocxTemplaterExporting}
          />
          <ReportExportDialog
            open={isExportModalOpen}
            setOpen={() => setIsExportModalOpen(false)}
            onExport={handleExport}
            selectedExportType={selectedExportType}
            setSelectedExportType={setSelectedExportType}
            isExporting={isExporting}
            report={report}
            onRetrySuccess={async () => {
              await reportQuery.refetch();
            }}
            isReportLoading={reportQuery.isFetching}
          />
        </>
      )}
      <AssessmentSlideOver
        title={
          isDiagnosisEvaluation
            ? assessmentFileForSearch?.originalFileName || "File Viewer"
            : "Previous Assessment Report"
        }
        open={assessmentSearchOpen}
        setOpen={setAssessmentSearchOpen}
      >
        <AssessmentReportSearch learnerFile={assessmentFileForSearch} />
      </AssessmentSlideOver>
    </>
  );
};
